import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Plus, Trash2, Sparkles, FileText, GitBranch, FormInput, CheckCircle2, Loader2 } from "lucide-react";
import WizardInlineHelp from "@/components/ai/WizardInlineHelp";

// =============================================================================
// MA-ARCH-142A: SCRIPTASSIST LOADING EXPERIENCE
// =============================================================================

const LOADING_MESSAGES = [
  { text: "Creating conversation structure...", icon: GitBranch, duration: 4000 },
  { text: "Generating questions and branches...", icon: FileText, duration: 5000 },
  { text: "Adding data collection fields...", icon: FormInput, duration: 5000 },
  { text: "Building objection handlers...", icon: GitBranch, duration: 4000 },
  { text: "Optimizing script flow...", icon: Sparkles, duration: 4000 },
  { text: "Finalizing your script...", icon: CheckCircle2, duration: 6000 },
];

function ScriptAssistLoadingState({ startTime, messageVariant }) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [progressValue, setProgressValue] = useState(0);
  const intervalRef = useRef(null);
  
  // Cycle through messages
  useEffect(() => {
    const cycleMessages = () => {
      setMessageIndex(prev => (prev + 1) % LOADING_MESSAGES.length);
    };
    
    intervalRef.current = setInterval(cycleMessages, LOADING_MESSAGES[messageIndex].duration);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [messageIndex]);
  
  // Simulate progress (30 second estimated time for script generation)
  useEffect(() => {
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const estimatedTotal = 30000; // 30 seconds
      // Progress caps at 95% to avoid showing 100% before completion
      const progress = Math.min(95, (elapsed / estimatedTotal) * 100);
      setProgressValue(progress);
    }, 300);
    
    return () => clearInterval(progressInterval);
  }, [startTime]);
  
  const currentMessage = LOADING_MESSAGES[messageIndex];
  const CurrentIcon = currentMessage.icon;
  
  return (
    <div className="py-8" data-testid="scriptassist-loading-state">
      <div className="flex flex-col items-center gap-6 text-center">
        {/* Animated icon container */}
        <div className="relative">
          {/* Outer pulse ring */}
          <div className="absolute inset-0 rounded-full bg-violet-500/20 animate-ping" style={{ animationDuration: '2s' }} />
          {/* Inner glow */}
          <div className="absolute inset-2 rounded-full bg-violet-500/30 animate-pulse" />
          {/* Icon container */}
          <div className="relative p-6 rounded-full bg-gradient-to-br from-violet-500/20 to-purple-600/20 border border-violet-500/30">
            <CurrentIcon className="h-10 w-10 text-violet-400 animate-pulse" />
          </div>
        </div>
        
        {/* Main message */}
        <div className="space-y-2 min-h-[50px] flex flex-col justify-center">
          <p className="text-lg font-medium text-violet-300 transition-all duration-500">
            Generating your script...
          </p>
          <p className="text-sm text-violet-400/80 animate-pulse">
            {currentMessage.text}
          </p>
        </div>
        
        {/* Message context hint */}
        {messageVariant && (
          <div className="text-xs text-slate-500 bg-slate-800/50 px-3 py-1.5 rounded-full">
            Aligning to message: <span className="text-violet-400">{messageVariant.replace(/_/g, ' ')}</span>
          </div>
        )}
        
        {/* Progress bar */}
        <div className="w-full max-w-sm space-y-2">
          <Progress 
            value={progressValue} 
            className="h-2 bg-slate-700/50 [&>div]:bg-gradient-to-r [&>div]:from-violet-500 [&>div]:to-purple-500 [&>div]:transition-all [&>div]:duration-300"
          />
          <div className="flex justify-between text-xs text-slate-500">
            <span>Processing...</span>
            <span>{Math.round(progressValue)}%</span>
          </div>
        </div>
        
        {/* Time estimate */}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>This typically takes 15-30 seconds</span>
        </div>
      </div>
    </div>
  );
}

export default function AiScriptWizardDialog({
  open,
  onOpenChange,
  state,
  actions,
  presets,
}) {
  const {
    llmKeyStatus,
    aiStep,
    aiProvider,
    aiModel,
    aiApiKey,
    aiTitle,
    aiDescription,
    aiGoal,
    aiAudience,
    aiTone,
    aiToneCustom,
    aiConversationStyle,
    aiConversationStyleCustom,
    aiStagesText,
    aiObjectionsText,
    aiDataFields,
    aiQuoteFields,
    aiGenerating,
    aiSpec,
    // MA-ARCH-142A: Message fields and loading state
    aiMessageVariant,
    aiGeneratingStartTime,
  } = state;

  const {
    setAiStep,
    setAiProvider,
    setAiModel,
    setAiApiKey,
    setAiTitle,
    setAiDescription,
    setAiGoal,
    setAiAudience,
    setAiTone,
    setAiToneCustom,
    setAiConversationStyle,
    setAiConversationStyleCustom,
    setAiStagesText,
    setAiObjectionsText,
    setAiDataFields,
    setAiQuoteFields,
    onGenerateAiSpec,
    onCreateFromAiSpec,
    toast,
  } = actions;

  const { dataFieldLabelPresets, quoteFieldLabelPresets } = presets;

  // MA-ARCH-142A: Show loading state when generating
  const showLoadingState = aiGenerating && aiStep === 5;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/10 sm:max-w-3xl" data-testid="ai-script-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-100">ScriptAssist</DialogTitle>
          <DialogDescription className="text-slate-200/70">
            Answer a few questions to generate a complete script. You’ll review it before it’s created.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between text-xs text-slate-200/70">
          <div>Step {aiStep} of 6</div>
          <div className="text-slate-300/70">Provider → Basics → Structure → Data → Quote → Review</div>
        </div>

        {aiStep === 1 ? (
          <WizardInlineHelp>
            Choose which AI provider will generate your script. If you’ve already connected a key in Admin → AI, you can leave the API key blank here.
          </WizardInlineHelp>
        ) : null}

        {aiStep === 2 ? (
          <WizardInlineHelp>
            Give ScriptAssist enough context to write your script: what it’s called, who it’s for, your goal, and how you want it framed (Conversation Style) + delivered (Tone).

            {"\n\n"}Tone affects delivery only. Conversation Style determines framing, structure, and persuasion logic.
          </WizardInlineHelp>
        ) : null}

        {aiStep === 3 ? (
          <WizardInlineHelp>
            Define the overall structure: stages (the main parts of the call) and objections (common pushbacks). ScriptAssist will create enough branches to cover these.
          </WizardInlineHelp>
        ) : null}

        {aiStep === 4 ? (
          <WizardInlineHelp>
            Add the fields you want captured during the conversation (name, phone, health info, etc.). ScriptAssist will place each field directly under the question where it’s asked.
          </WizardInlineHelp>
        ) : null}

        {aiStep === 5 ? (
          <WizardInlineHelp>
            Define quote sheet fields you’ll want available while building the quote (coverage amount, premium, options, etc.). These are stored at the script level.
          </WizardInlineHelp>
        ) : null}

        {aiStep === 6 ? (
          <WizardInlineHelp>
            Review the generated JSON before creating the script. When you click Create, MissionAssist will convert it into Nodes/Options/Fields and enforce Script Blocks automatically.
          </WizardInlineHelp>
        ) : null}

        {aiStep === 1 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ai-provider" className="text-slate-200/80">
                  Provider
                </Label>
                <Select value={aiProvider} onValueChange={setAiProvider}>
                  <input id="ai-provider" className="hidden" aria-hidden="true" />
                  <SelectTrigger className="bg-white/5 text-slate-100 ring-1 ring-white/10">
                    <SelectValue placeholder="Choose provider" />
                  </SelectTrigger>
                  <SelectContent className="glass border-white/10">
                    <SelectItem value="openai">OpenAI (ChatGPT)</SelectItem>
                    <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-slate-300/70">
                  Connected: {aiProvider === "openai" ? (llmKeyStatus.openai ? "Yes" : "No") : llmKeyStatus.anthropic ? "Yes" : "No"}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ai-model" className="text-slate-200/80">
                  Model
                </Label>
                <Input
                  id="ai-model"
                  aria-label="Model"
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                  className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                  placeholder={aiProvider === "openai" ? "gpt-5.1" : "claude-4-sonnet-20250514"}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-api-key" className="text-slate-200/80">
                API key
              </Label>
              <Input
                id="ai-api-key"
                aria-label="API key"
                value={aiApiKey}
                onChange={(e) => setAiApiKey(e.target.value)}
                className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                placeholder={aiProvider === "openai" ? "sk-..." : "sk-ant-..."}
                type="password"
              />
              <div className="text-xs text-slate-300/70">If you already connected a key, you can leave this blank.</div>
              <div className="text-xs text-slate-300/70">
                ScriptAssist will automatically create separate Script Blocks for each question and each field (Prospects scope) so fields align cleanly.
              </div>
            </div>
          </div>
        ) : null}

        {aiStep === 2 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="ai-script-title" className="text-slate-200/80">
                  Script title
                </Label>
                <Input
                  id="ai-script-title"
                  aria-label="Script title"
                  value={aiTitle}
                  onChange={(e) => setAiTitle(e.target.value)}
                  className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                  placeholder="Mortgage Protection — Opener"
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="ai-script-description" className="text-slate-200/80">
                  Description (optional)
                </Label>
                <Textarea
                  id="ai-script-description"
                  aria-label="Description (optional)"
                  value={aiDescription}
                  onChange={(e) => setAiDescription(e.target.value)}
                  className="min-h-[80px] bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                />
              </div>

            <div className="space-y-2">
              <Label htmlFor="ai-script-goal" className="text-slate-200/80">
                Goal
              </Label>
              <Textarea
                id="ai-script-goal"
                aria-label="Goal"
                value={aiGoal}
                onChange={(e) => setAiGoal(e.target.value)}
                className="min-h-[80px] bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                placeholder="What should the agent accomplish?"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-script-audience" className="text-slate-200/80">
                Audience
              </Label>
              <Textarea
                id="ai-script-audience"
                aria-label="Audience"
                value={aiAudience}
                onChange={(e) => setAiAudience(e.target.value)}
                className="min-h-[80px] bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                placeholder="Who is the prospect?"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-script-tone" className="text-slate-200/80">
                Tone
              </Label>
              <Select value={aiTone} onValueChange={setAiTone}>
                <input id="ai-script-tone" className="hidden" aria-hidden="true" />
                <SelectTrigger className="bg-white/5 text-slate-100 ring-1 ring-white/10">
                  <SelectValue placeholder="Select tone" />
                </SelectTrigger>
                <SelectContent className="glass border-white/10">
                <SelectItem value="Professional & Calm">Professional & Calm</SelectItem>
                <SelectItem value="Friendly & Conversational">Friendly & Conversational</SelectItem>
                <SelectItem value="Direct & Straightforward">Direct & Straightforward</SelectItem>
                <SelectItem value="Empathetic & Reassuring">Empathetic & Reassuring</SelectItem>
                <SelectItem value="Confident & Decisive">Confident & Decisive</SelectItem>
                <SelectItem value="Educational & Neutral">Educational & Neutral</SelectItem>
                <SelectItem value="Upbeat & Encouraging (Optional)">Upbeat & Encouraging (Optional)</SelectItem>
                <SelectItem value="Custom">Custom</SelectItem>
              </SelectContent>
              </Select>
              <div className="text-xs text-slate-300/70">Tone affects delivery only (wording/feel), not the structure.</div>

              {aiTone === "Custom" ? (
                <Input
                  aria-label="Custom tone"
                  value={aiToneCustom}
                  onChange={(e) => setAiToneCustom(e.target.value)}
                  className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                  placeholder="Describe the tone you want (e.g., 'optimistic but grounded, no hype')"
                />
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-script-conversation-style" className="text-slate-200/80">
                Conversation Style <span className="text-rose-300">*</span>
              </Label>
              <Select value={aiConversationStyle} onValueChange={setAiConversationStyle}>
                <input id="ai-script-conversation-style" className="hidden" aria-hidden="true" />
                <SelectTrigger className="bg-white/5 text-slate-100 ring-1 ring-white/10">
                  <SelectValue placeholder="Select a conversation style" />
                </SelectTrigger>
                <SelectContent className="glass border-white/10">
                  {/* placeholder is handled by <SelectValue placeholder="Select a conversation style" /> */}
                  <SelectItem value="Protect What You Have">Protect What You Have</SelectItem>
                  <SelectItem value="Build a Better Outcome">Build a Better Outcome</SelectItem>
                  <SelectItem value="Expert Recommendation">Expert Recommendation</SelectItem>
                  <SelectItem value="Needs-Based Conversation">Needs-Based Conversation</SelectItem>
                  <SelectItem value="Challenge the Status Quo">Challenge the Status Quo</SelectItem>
                  <SelectItem value="What Others Like You Chose">What Others Like You Chose</SelectItem>
                  <SelectItem value="Low-Risk, Flexible Option">Low-Risk, Flexible Option</SelectItem>
                  <SelectItem value="Step-by-Step Agreement">Step-by-Step Agreement</SelectItem>
                  <SelectItem value="Custom">Custom</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-300/70">Conversation Style determines framing, structure, and persuasion logic.</div>

              {aiConversationStyle === "Custom" ? (
                <Input
                  aria-label="Custom conversation style"
                  value={aiConversationStyleCustom}
                  onChange={(e) => setAiConversationStyleCustom(e.target.value)}
                  className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                  placeholder="Describe the conversation style you want (e.g., 'Socratic, question-first, zero pressure')"
                />
              ) : null}
            </div>
            </div>
          </div>
        ) : null}

        {aiStep === 3 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ai-stages" className="text-slate-200/80">
                Stages (one per line)
              </Label>
              <Textarea
                id="ai-stages"
                aria-label="Stages (one per line)"
                value={aiStagesText}
                onChange={(e) => setAiStagesText(e.target.value)}
                className="min-h-[170px] bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
              />
            </div>
              <div className="space-y-2">
                <Label htmlFor="ai-objections" className="text-slate-200/80">
                Objections (one per line)
              </Label>
                <Textarea
                  id="ai-objections"
                  aria-label="Objections (one per line)"
                  value={aiObjectionsText}
                  onChange={(e) => setAiObjectionsText(e.target.value)}
                  className="min-h-[170px] bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                />
              </div>
            </div>
          </div>
        ) : null}

        {aiStep === 4 ? (
          <div className="space-y-4">
            <div>
              <div className="text-sm text-slate-100">Data capture fields</div>
              <div className="mt-1 text-xs text-slate-300/70">Add fields similar to Builder (preset/custom + type + scope).</div>
            </div>

            <div className="space-y-3">
              {(aiDataFields || []).map((row) => (
                <div key={row.id} className="grid grid-cols-1 gap-3 rounded-2xl border border-white/10 bg-white/5 p-3 md:grid-cols-12">
                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-300/70">Label</div>
                    <Select
                      value={row.label_mode}
                      onValueChange={(v) =>
                        setAiDataFields((prev) => prev.map((x) => (x.id === row.id ? { ...x, label_mode: v } : x)))
                      }
                    >
                      <SelectTrigger className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10">
                        <SelectValue placeholder="Preset" />
                      </SelectTrigger>
                      <SelectContent className="glass border-white/10">
                        <SelectItem value="preset">Preset</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-4">
                    {row.label_mode === "preset" ? (
                      <>
                        <div className="text-xs text-slate-300/70">Preset</div>
                        <Select
                          value={row.preset_label}
                          onValueChange={(v) => setAiDataFields((prev) => prev.map((x) => (x.id === row.id ? { ...x, preset_label: v } : x)))}
                        >
                          <SelectTrigger className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10">
                            <SelectValue placeholder="Choose" />
                          </SelectTrigger>
                          <SelectContent className="glass border-white/10">
                            {dataFieldLabelPresets.map((l) => (
                              <SelectItem key={l} value={l}>
                                {l}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    ) : (
                      <>
                        <div className="text-xs text-slate-300/70">Custom label</div>
                        <Input
                          className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10"
                          value={row.custom_label}
                          onChange={(e) => setAiDataFields((prev) => prev.map((x) => (x.id === row.id ? { ...x, custom_label: e.target.value } : x)))}
                        />
                      </>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-300/70">Type</div>
                    <Select
                      value={row.field_type}
                      onValueChange={(v) =>
                        setAiDataFields((prev) =>
                          prev.map((x) => (x.id === row.id ? { ...x, field_type: v, options_text: v !== "dropdown" ? "" : x.options_text } : x)),
                        )
                      }
                    >
                      <SelectTrigger className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent className="glass border-white/10">
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="textarea">Long text</SelectItem>
                        <SelectItem value="checkbox">Checkbox</SelectItem>
                        <SelectItem value="dropdown">Dropdown</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-300/70">Scope</div>
                    <Select value={row.scope} onValueChange={(v) => setAiDataFields((prev) => prev.map((x) => (x.id === row.id ? { ...x, scope: v } : x)))}>
                      <SelectTrigger className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10">
                        <SelectValue placeholder="Scope" />
                      </SelectTrigger>
                      <SelectContent className="glass border-white/10">
                        <SelectItem value="household">Household</SelectItem>
                        <SelectItem value="prospects">Prospects</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2 md:col-span-2 md:justify-end">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={!!row.required}
                        onCheckedChange={(v) => setAiDataFields((prev) => prev.map((x) => (x.id === row.id ? { ...x, required: !!v } : x)))}
                        id={`req-${row.id}`}
                      />
                      <Label htmlFor={`req-${row.id}`} className="text-sm text-slate-200/80">
                        Required
                      </Label>
                    </div>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="rounded-full bg-white/10 text-rose-300 ring-1 ring-white/10 hover:bg-white/15"
                      onClick={() => setAiDataFields((prev) => prev.filter((x) => x.id !== row.id))}
                      title="Remove"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {row.field_type === "dropdown" ? (
                    <div className="md:col-span-12">
                      <div className="text-xs text-slate-300/70">Dropdown options (comma-separated)</div>
                      <Input
                        className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10"
                        value={row.options_text}
                        onChange={(e) => setAiDataFields((prev) => prev.map((x) => (x.id === row.id ? { ...x, options_text: e.target.value } : x)))}
                      />
                    </div>
                  ) : null}
                </div>
              ))}

              <div>
                <Button
                  variant="secondary"
                  className="rounded-full bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
                  onClick={() =>
                    setAiDataFields((prev) => [
                      ...prev,
                      {
                        id: crypto.randomUUID(),
                        label_mode: "preset",
                        preset_label: dataFieldLabelPresets[0],
                        custom_label: "",
                        field_type: "text",
                        scope: "household",
                        required: false,
                        options_text: "",
                      },
                    ])
                  }
                >
                  <Plus className="h-4 w-4" />
                  Add data field
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {aiStep === 5 ? (
          showLoadingState ? (
            <ScriptAssistLoadingState 
              startTime={aiGeneratingStartTime || Date.now()} 
              messageVariant={aiMessageVariant}
            />
          ) : (
          <div className="space-y-4">
            <div>
              <div className="text-sm text-slate-100">Quote sheet fields</div>
              <div className="mt-1 text-xs text-slate-300/70">Add quote sheet fields similar to Builder.</div>
            </div>

            <div className="space-y-3">
              {(aiQuoteFields || []).map((row) => (
                <div key={row.id} className="grid grid-cols-1 gap-3 rounded-2xl border border-white/10 bg-white/5 p-3 md:grid-cols-12">
                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-300/70">Label</div>
                    <Select value={row.label_mode} onValueChange={(v) => setAiQuoteFields((prev) => prev.map((x) => (x.id === row.id ? { ...x, label_mode: v } : x)))}>
                      <SelectTrigger className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10">
                        <SelectValue placeholder="Preset" />
                      </SelectTrigger>
                      <SelectContent className="glass border-white/10">
                        <SelectItem value="preset">Preset</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-3">
                    {row.label_mode === "preset" ? (
                      <>
                        <div className="text-xs text-slate-300/70">Preset</div>
                        <Select value={row.preset_label} onValueChange={(v) => setAiQuoteFields((prev) => prev.map((x) => (x.id === row.id ? { ...x, preset_label: v } : x)))}>
                          <SelectTrigger className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10">
                            <SelectValue placeholder="Choose" />
                          </SelectTrigger>
                          <SelectContent className="glass border-white/10">
                            {quoteFieldLabelPresets.map((l) => (
                              <SelectItem key={l} value={l}>
                                {l}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    ) : (
                      <>
                        <div className="text-xs text-slate-300/70">Custom label</div>
                        <Input
                          className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10"
                          value={row.custom_label}
                          onChange={(e) => setAiQuoteFields((prev) => prev.map((x) => (x.id === row.id ? { ...x, custom_label: e.target.value } : x)))}
                        />
                      </>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-300/70">Kind</div>
                    <Select
                      value={row.kind}
                      onValueChange={(v) =>
                        setAiQuoteFields((prev) =>
                          prev.map((x) =>
                            x.id === row.id
                              ? {
                                  ...x,
                                  kind: v,
                                  include_in_household: v === "input" ? x.include_in_household : false,
                                  show_in_household: v === "calculated" ? x.show_in_household : false,
                                }
                              : x,
                          ),
                        )
                      }
                    >
                      <SelectTrigger className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10">
                        <SelectValue placeholder="Kind" />
                      </SelectTrigger>
                      <SelectContent className="glass border-white/10">
                        <SelectItem value="input">Input</SelectItem>
                        <SelectItem value="calculated">Calculated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-300/70">Type</div>
                    <Select
                      value={row.field_type}
                      onValueChange={(v) =>
                        setAiQuoteFields((prev) =>
                          prev.map((x) => (x.id === row.id ? { ...x, field_type: v, options_text: v !== "dropdown" ? "" : x.options_text } : x)),
                        )
                      }
                    >
                      <SelectTrigger className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent className="glass border-white/10">
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="textarea">Long text</SelectItem>
                        <SelectItem value="dropdown">Dropdown</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2 md:col-span-3 md:justify-end">
                    {row.kind === "input" ? (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={!!row.include_in_household}
                          onCheckedChange={(v) => setAiQuoteFields((prev) => prev.map((x) => (x.id === row.id ? { ...x, include_in_household: !!v } : x)))}
                          id={`inc-${row.id}`}
                        />
                        <Label htmlFor={`inc-${row.id}`} className="text-sm text-slate-200/80">
                          Include in household
                        </Label>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={!!row.show_in_household}
                          onCheckedChange={(v) => setAiQuoteFields((prev) => prev.map((x) => (x.id === row.id ? { ...x, show_in_household: !!v } : x)))}
                          id={`show-${row.id}`}
                        />
                        <Label htmlFor={`show-${row.id}`} className="text-sm text-slate-200/80">
                          Show in household
                        </Label>
                      </div>
                    )}

                    <Button
                      variant="secondary"
                      size="icon"
                      className="rounded-full bg-white/10 text-rose-300 ring-1 ring-white/10 hover:bg-white/15"
                      onClick={() => setAiQuoteFields((prev) => prev.filter((x) => x.id !== row.id))}
                      title="Remove"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {row.field_type === "dropdown" ? (
                    <div className="md:col-span-12">
                      <div className="text-xs text-slate-300/70">Dropdown options (comma-separated)</div>
                      <Input
                        className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10"
                        value={row.options_text}
                        onChange={(e) => setAiQuoteFields((prev) => prev.map((x) => (x.id === row.id ? { ...x, options_text: e.target.value } : x)))}
                      />
                    </div>
                  ) : null}
                </div>
              ))}

              <div>
                <Button
                  variant="secondary"
                  className="rounded-full bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
                  onClick={() =>
                    setAiQuoteFields((prev) => [
                      ...prev,
                      {
                        id: crypto.randomUUID(),
                        label_mode: "preset",
                        preset_label: quoteFieldLabelPresets[0],
                        custom_label: "",
                        kind: "input",
                        field_type: "text",
                        include_in_household: false,
                        show_in_household: false,
                        options_text: "",
                      },
                    ])
                  }
                >
                  <Plus className="h-4 w-4" />
                  Add quote field
                </Button>
              </div>
            </div>
          </div>
          )
        ) : null}

        {aiStep === 6 ? (
          <div className="space-y-4">
            <div className="rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
              <div className="text-sm text-slate-100">Generated draft (review)</div>
              <div className="mt-1 text-xs text-slate-300/70">
                Nodes: {aiSpec?.nodes?.length || 0} • Options: {aiSpec?.options?.length || 0} • Fields: {aiSpec?.fields?.length || 0} • Quote fields: {aiSpec?.quote_fields?.length || 0}
              </div>
            </div>
            <div className="max-h-[260px] overflow-auto rounded-xl bg-black/20 p-3 text-xs text-slate-200/80 ring-1 ring-white/10">
              <pre className="whitespace-pre-wrap">{JSON.stringify(aiSpec, null, 2)}</pre>
            </div>
          </div>
        ) : null}

        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <Button
            variant="secondary"
            className="rounded-full bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
            onClick={() => {
              if (aiStep === 1) {
                onOpenChange(false);
                return;
              }
              setAiStep((s) => Math.max(1, s - 1));
            }}
            disabled={aiGenerating}
          >
            {aiStep === 1 ? "Cancel" : "Back"}
          </Button>

          <div className="flex items-center gap-2">
            {aiStep < 5 ? (
              <Button
                className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                onClick={() => {
                  if (aiStep === 2 && (!aiGoal.trim() || !aiAudience.trim() || !aiConversationStyle)) {
                    toast({
                      title: "Missing info",
                      description: "Please fill Goal, Audience, and Conversation Style.",
                      variant: "destructive",
                    });
                    return;
                  }
                  setAiStep((s) => Math.min(6, s + 1));
                }}
                disabled={aiGenerating || (aiStep === 2 && (!aiGoal.trim() || !aiAudience.trim() || !aiConversationStyle))}
              >
                Next
              </Button>
            ) : null}

            {aiStep === 5 ? (
              <Button
                className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                onClick={onGenerateAiSpec}
                disabled={aiGenerating || !aiGoal.trim() || !aiAudience.trim() || !aiConversationStyle}
              >
                {aiGenerating ? "Generating…" : "Generate"}
              </Button>
            ) : null}

            {aiStep === 6 ? (
              <Button
                className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                onClick={onCreateFromAiSpec}
                disabled={aiGenerating || !aiSpec}
              >
                {aiGenerating ? "Creating…" : "Create script"}
              </Button>
            ) : null}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
